import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { exportData, importData, clearAllData, saveData, setPasswordHash, setPinEncryptedPassword } from '@/lib/storage';
import { hashPassword, verifyPassword, encrypt } from '@/lib/crypto';
import { getPasswordHash } from '@/lib/storage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Download, Upload, KeyRound, Trash2, Moon, Timer, Tag, Plus, X, Hash, Fingerprint, Phone, Pencil, Check, User, MapPin, Mail, Palette, AlertTriangle, Trophy } from 'lucide-react';
import { THEMES, ThemeId, getStoredTheme, applyTheme } from '@/lib/theme';
import { isBiometricAvailable } from '@/lib/webauthn';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { supabase } from '@/integrations/supabase/client';

export default function SettingsPage() {
  const { data, updateData, password, lock, setupPin, removePin, hasPin, setupBiometric, removeBiometric, hasBio, storedEmail, updateStoredEmail } = useAuth();
  const { toast } = useToast();

  // Change password state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);

  // Import state
  const [importPwd, setImportPwd] = useState('');
  const [importFile, setImportFile] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  // PIN state
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [settingPin, setSettingPin] = useState(false);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [settingBio, setSettingBio] = useState(false);

  // PII state
  type PiiData = {
    mobile: string;
    date_of_birth: string;
    address_line1: string;
    address_line2: string;
    suburb: string;
    state: string;
    postcode: string;
  };
  const EMPTY_PII: PiiData = { mobile: '', date_of_birth: '', address_line1: '', address_line2: '', suburb: '', state: '', postcode: '' };
  const [pii, setPii] = useState<PiiData>(EMPTY_PII);
  const [piiEdit, setPiiEdit] = useState<PiiData>(EMPTY_PII);
  const [piiEditing, setPiiEditing] = useState(false);
  const [piiSaving, setPiiSaving] = useState(false);
  const [piiLoading, setPiiLoading] = useState(true);

  // Display name state (from profiles table)
  const [displayName, setDisplayName] = useState('');
  const [displayNameEdit, setDisplayNameEdit] = useState('');
  const [displayNameEditing, setDisplayNameEditing] = useState(false);
  const [displayNameSaving, setDisplayNameSaving] = useState(false);

  // Email change state
  const [emailEdit, setEmailEdit] = useState('');
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailConfirmPwd, setEmailConfirmPwd] = useState('');
  const [activeTheme, setActiveTheme] = useState<ThemeId>(getStoredTheme);

  useEffect(() => {
    isBiometricAvailable().then(setBioAvailable);
  }, []);

  // Load PII + display_name in parallel on mount
  useEffect(() => {
    let cancelled = false;
    async function loadProfileData() {
      setPiiLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;
      const [piiRes, profileRes] = await Promise.all([
        supabase.from('user_pii' as any)
          .select('mobile, date_of_birth, address_line1, address_line2, suburb, state, postcode')
          .eq('user_id', user.id).maybeSingle(),
        supabase.from('profiles')
          .select('display_name')
          .eq('user_id', user.id).maybeSingle(),
      ]);
      if (!cancelled) {
        const d = piiRes.data as any;
        setPii({
          mobile: d?.mobile ?? '',
          date_of_birth: d?.date_of_birth ?? '',
          address_line1: d?.address_line1 ?? '',
          address_line2: d?.address_line2 ?? '',
          suburb: d?.suburb ?? '',
          state: d?.state ?? '',
          postcode: d?.postcode ?? '',
        });
        const dn = (profileRes.data as any)?.display_name ?? '';
        setDisplayName(dn);
        setPiiLoading(false);
      }
    }
    loadProfileData();
    return () => { cancelled = true; };
  }, []);
  const LAST_BACKUP_KEY = 'aussibooks_last_backup';
  const [lastBackup, setLastBackup] = useState<string | null>(localStorage.getItem(LAST_BACKUP_KEY));

  const handleExport = async () => {
    try {
      const encrypted = await exportData(password);
      const blob = new Blob([encrypted], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aussibooks-backup-${new Date().toISOString().slice(0, 10)}.enc`;
      a.click();
      URL.revokeObjectURL(url);
      const now = new Date().toISOString();
      localStorage.setItem(LAST_BACKUP_KEY, now);
      setLastBackup(now);
      toast({ title: 'Export successful', description: 'Your encrypted backup has been downloaded.' });
    } catch {
      toast({ title: 'Export failed', variant: 'destructive' });
    }
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImportFile(reader.result as string);
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const pwd = importPwd || password;
      const imported = await importData(importFile, pwd);
      await updateData(() => imported);
      toast({ title: 'Import successful', description: 'Your data has been restored.' });
      setImportFile(null);
      setImportPwd('');
    } catch {
      toast({ title: 'Import failed', description: 'Wrong password or corrupted file.', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPwd !== confirmPwd) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    if (newPwd.length < 8) {
      toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
      return;
    }
    setChangingPwd(true);
    try {
      const valid = await verifyPassword(currentPwd, getPasswordHash()!);
      if (!valid) {
        toast({ title: 'Current password incorrect', variant: 'destructive' });
        return;
      }
      const newHash = await hashPassword(newPwd);
      setPasswordHash(newHash);
      // Re-encrypt data with new password
      await saveData(data, newPwd);
      // Clear PIN and biometric since master password changed
      if (hasPin) removePin();
      if (hasBio) removeBiometric();
      const clearedItems = [hasPin && 'PIN', hasBio && 'Biometric'].filter(Boolean).join(' and ');
      toast({
        title: 'Password changed',
        description: clearedItems
          ? `${clearedItems} cleared. Please re-set and re-login.`
          : 'Please re-login with your new password.',
      });
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      lock();
    } catch {
      toast({ title: 'Failed to change password', variant: 'destructive' });
    } finally {
      setChangingPwd(false);
    }
  };

  const handleSetPin = async () => {
    if (pinValue.length < 4 || pinValue.length > 6) {
      toast({ title: 'PIN must be 4–6 digits', variant: 'destructive' });
      return;
    }
    if (pinValue !== pinConfirm) {
      toast({ title: 'PINs do not match', variant: 'destructive' });
      return;
    }
    setSettingPin(true);
    try {
      await setupPin(pinValue);
      toast({ title: 'PIN set', description: 'You can now unlock with your PIN.' });
      setPinValue('');
      setPinConfirm('');
    } catch {
      toast({ title: 'Failed to set PIN', variant: 'destructive' });
    } finally {
      setSettingPin(false);
    }
  };

  const handleRemovePin = () => {
    removePin();
    toast({ title: 'PIN removed' });
  };

  const handleSetupBiometric = async () => {
    setSettingBio(true);
    try {
      await setupBiometric();
      toast({ title: 'Biometric enabled', description: 'You can now unlock with fingerprint or face.' });
    } catch {
      toast({ title: 'Failed to set up biometric', description: 'Your device may not support this feature.', variant: 'destructive' });
    } finally {
      setSettingBio(false);
    }
  };

  const handleRemoveBiometric = () => {
    removeBiometric();
    toast({ title: 'Biometric removed' });
  };

  const MOBILE_RE = /^\+?[0-9\s\-().]{7,20}$/;
  const POSTCODE_RE = /^\d{4}$/;
  const AUS_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

  const handleSavePii = async () => {
    const m = piiEdit.mobile.trim();
    if (m && !MOBILE_RE.test(m)) {
      toast({ title: 'Invalid mobile number', description: 'Enter a valid phone number.', variant: 'destructive' });
      return;
    }
    const pc = piiEdit.postcode.trim();
    if (pc && !POSTCODE_RE.test(pc)) {
      toast({ title: 'Invalid postcode', description: 'Australian postcodes are 4 digits.', variant: 'destructive' });
      return;
    }
    setPiiSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not signed in');
      const payload = {
        user_id: user.id,
        mobile: m || null,
        date_of_birth: piiEdit.date_of_birth || null,
        address_line1: piiEdit.address_line1.trim() || null,
        address_line2: piiEdit.address_line2.trim() || null,
        suburb: piiEdit.suburb.trim() || null,
        state: piiEdit.state || null,
        postcode: pc || null,
      };
      const { error } = await supabase.from('user_pii' as any).upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;
      setPii({ ...piiEdit, mobile: m, postcode: pc });
      setPiiEditing(false);
      toast({ title: 'Personal details saved' });
    } catch {
      toast({ title: 'Failed to save details', variant: 'destructive' });
    } finally {
      setPiiSaving(false);
    }
  };

  const piiHasData = Object.values(pii).some(v => v);

  const handleSaveDisplayName = async () => {
    const trimmed = displayNameEdit.trim().slice(0, 60);
    setDisplayNameSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not signed in');
      const { error } = await supabase
        .from('profiles')
        .update({ display_name: trimmed || null })
        .eq('user_id', user.id);
      if (error) throw error;
      setDisplayName(trimmed);
      setDisplayNameEditing(false);
      toast({ title: 'Display name saved' });
    } catch {
      toast({ title: 'Failed to save display name', variant: 'destructive' });
    } finally {
      setDisplayNameSaving(false);
    }
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSaveEmail = async () => {
    const newEmail = emailEdit.trim().toLowerCase();
    if (!EMAIL_RE.test(newEmail)) {
      toast({ title: 'Invalid email address', variant: 'destructive' });
      return;
    }
    if (newEmail === storedEmail.toLowerCase()) {
      toast({ title: "That's already your email", variant: 'destructive' });
      return;
    }
    if (!emailConfirmPwd) {
      toast({ title: 'Enter your password to confirm', variant: 'destructive' });
      return;
    }
    const valid = await verifyPassword(emailConfirmPwd, getPasswordHash()!);
    if (!valid) {
      toast({ title: 'Incorrect password', variant: 'destructive' });
      return;
    }
    setEmailSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      updateStoredEmail(newEmail);
      setEmailEditing(false);
      setEmailEdit('');
      setEmailConfirmPwd('');
      toast({
        title: 'Confirmation emails sent',
        description: 'Check both your old and new inbox to confirm the change.',
      });
    } catch (e: any) {
      toast({ title: 'Failed to update email', description: e?.message, variant: 'destructive' });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleClearAll = async () => {
    // Clear basiq_user_id from profiles in database
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('profiles').update({ basiq_user_id: null } as any).eq('user_id', user.id);
      }
    } catch { /* best effort */ }
    // Sign out from auth
    await supabase.auth.signOut().catch(() => {});
    // Clear all local data
    clearAllData();
    toast({ title: 'All data cleared' });
    window.location.reload();
  };

  const handleResetAchievements = async () => {
    await updateData(prev => ({
      ...prev,
      settings: { ...prev.settings, achievementsResetAt: new Date().toISOString() },
    }));
    toast({ title: 'Achievements reset', description: 'All badges have been cleared. Earn them again from now!' });
  };

  const handleDarkMode = async (enabled: boolean) => {
    updateData(prev => ({
      ...prev,
      settings: { ...prev.settings, darkMode: enabled },
    }));
    const { applyDarkMode } = await import('@/lib/theme');
    applyDarkMode(enabled);
  };

  const addCategory = async () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    if (data.categories.includes(trimmed)) {
      toast({ title: 'Category already exists', variant: 'destructive' });
      return;
    }
    await updateData(prev => ({ ...prev, categories: [...prev.categories, trimmed].sort() }));
    setNewCategory('');
    toast({ title: `"${trimmed}" added` });
  };

  const removeCategory = async (cat: string) => {
    await updateData(prev => ({ ...prev, categories: prev.categories.filter(c => c !== cat) }));
    toast({ title: `"${cat}" removed` });
  };

  const handleAutoLock = (value: string) => {
    updateData(prev => ({
      ...prev,
      settings: { ...prev.settings, autoLockMinutes: parseInt(value) },
    }));
  };

  return (
    <div className="px-4 pt-6 pb-24">
      <h1 className="mb-4 font-display text-2xl font-bold">Settings</h1>

      <div className="space-y-4">
        {/* Subscription */}
        <SubscriptionCard />

        {/* Personal Details (PII) */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Personal Details</p>
                  <p className="text-xs text-muted-foreground">Stored securely, used for bank sync &amp; future features</p>
                </div>
              </div>
              {!piiLoading && !piiEditing && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setPiiEdit(pii); setPiiEditing(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                  {piiHasData ? 'Edit' : 'Add'}
                </Button>
              )}
            </div>

            {/* Display name — always visible, separate save */}
            <div className="mb-4 pb-4 border-b border-border">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Display Name</label>
              {displayNameEditing ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Your name"
                    maxLength={60}
                    value={displayNameEdit}
                    onChange={e => setDisplayNameEdit(e.target.value)}
                    autoFocus
                    className="flex-1"
                  />
                  <Button size="icon" onClick={handleSaveDisplayName} disabled={displayNameSaving}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline" onClick={() => setDisplayNameEditing(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {piiLoading ? '…' : displayName || <span className="italic">Not set</span>}
                  </span>
                  <Button size="sm" variant="ghost" className="gap-1.5 h-7 px-2" onClick={() => { setDisplayNameEdit(displayName); setDisplayNameEditing(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                    {displayName ? 'Edit' : 'Add'}
                  </Button>
                </div>
              )}
            </div>

            {piiLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : piiEditing ? (
              <div className="space-y-3">
                {/* Mobile */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Mobile</label>
                  <Input type="tel" inputMode="tel" placeholder="+61 4xx xxx xxx" maxLength={20}
                    value={piiEdit.mobile} onChange={e => setPiiEdit(p => ({ ...p, mobile: e.target.value }))} />
                </div>
                {/* Date of birth */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Date of Birth</label>
                  <Input type="date" max={new Date().toISOString().slice(0, 10)}
                    value={piiEdit.date_of_birth} onChange={e => setPiiEdit(p => ({ ...p, date_of_birth: e.target.value }))} />
                </div>
                {/* Address */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Address</label>
                  <div className="space-y-2">
                    <Input placeholder="Street address" maxLength={100}
                      value={piiEdit.address_line1} onChange={e => setPiiEdit(p => ({ ...p, address_line1: e.target.value }))} />
                    <Input placeholder="Apt / unit / suite (optional)" maxLength={100}
                      value={piiEdit.address_line2} onChange={e => setPiiEdit(p => ({ ...p, address_line2: e.target.value }))} />
                    <div className="grid grid-cols-5 gap-2">
                      <Input placeholder="Suburb" maxLength={60} className="col-span-2"
                        value={piiEdit.suburb} onChange={e => setPiiEdit(p => ({ ...p, suburb: e.target.value }))} />
                      <Select value={piiEdit.state} onValueChange={v => setPiiEdit(p => ({ ...p, state: v }))}>
                        <SelectTrigger className="col-span-2"><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>
                          {AUS_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input placeholder="PC" inputMode="numeric" maxLength={4}
                        value={piiEdit.postcode} onChange={e => setPiiEdit(p => ({ ...p, postcode: e.target.value.replace(/\D/g, '') }))} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button className="flex-1 gap-1.5" onClick={handleSavePii} disabled={piiSaving}>
                    <Check className="h-4 w-4" />{piiSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setPiiEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : piiHasData ? (
              <dl className="space-y-1.5 text-sm">
                {pii.mobile && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Mobile</dt><dd>{pii.mobile}</dd></div>}
                {pii.date_of_birth && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Date of birth</dt><dd>{new Date(pii.date_of_birth + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</dd></div>}
                {pii.address_line1 && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-24 shrink-0">Address</dt>
                    <dd className="leading-snug">
                      {pii.address_line1}{pii.address_line2 ? `, ${pii.address_line2}` : ''}<br />
                      {[pii.suburb, pii.state, pii.postcode].filter(Boolean).join(' ')}
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground italic">No details added yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Themes */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Palette className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Theme</p>
                <p className="text-xs text-muted-foreground">Choose your visual style</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {THEMES.map(theme => (
                <button
                  key={theme.id}
                  onClick={() => { applyTheme(theme.id); setActiveTheme(theme.id); }}
                  className={`relative rounded-lg border-2 p-2.5 text-left transition-all ${
                    activeTheme === theme.id
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  {/* Mini preview */}
                  <div className="flex gap-1 mb-2">
                    <div className="h-8 w-4 rounded" style={{ backgroundColor: theme.preview.sidebar }} />
                    <div className="flex-1 rounded p-1" style={{ backgroundColor: theme.preview.bg }}>
                      <div className="h-1.5 w-8 rounded-full mb-1" style={{ backgroundColor: theme.preview.primary }} />
                      <div className="h-1.5 w-5 rounded-full" style={{ backgroundColor: theme.preview.accent }} />
                    </div>
                  </div>
                  <p className="text-xs font-semibold leading-tight">{theme.name}</p>
                  <p className="text-[10px] text-muted-foreground leading-snug">{theme.description}</p>
                  {activeTheme === theme.id && (
                    <div className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Dark Mode */}
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Moon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Dark Mode</p>
                <p className="text-xs text-muted-foreground">Switch to dark theme</p>
              </div>
            </div>
            <Switch
              checked={data.settings.darkMode}
              onCheckedChange={handleDarkMode}
            />
          </CardContent>
        </Card>

        {/* Auto-Lock Timer */}
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Timer className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Auto-Lock Timer</p>
                <p className="text-xs text-muted-foreground">Lock after inactivity</p>
              </div>
            </div>
            <Select value={String(data.settings.autoLockMinutes)} onValueChange={handleAutoLock}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 min</SelectItem>
                <SelectItem value="5">5 min</SelectItem>
                <SelectItem value="15">15 min</SelectItem>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="0">Never</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Quick PIN */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Hash className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Quick PIN</p>
                <p className="text-xs text-muted-foreground">
                  {hasPin ? 'PIN is active — unlock faster' : 'Set a 4–6 digit PIN for faster unlock'}
                </p>
              </div>
            </div>
            {hasPin ? (
              <Button variant="outline" className="w-full" onClick={handleRemovePin}>Remove PIN</Button>
            ) : (
              <div className="space-y-2">
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Enter PIN (4–6 digits)"
                  value={pinValue}
                  onChange={e => setPinValue(e.target.value.replace(/\D/g, ''))}
                  className="text-center"
                />
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="Confirm PIN"
                  value={pinConfirm}
                  onChange={e => setPinConfirm(e.target.value.replace(/\D/g, ''))}
                  className="text-center"
                />
                <Button className="w-full" onClick={handleSetPin} disabled={settingPin}>
                  {settingPin ? 'Setting…' : 'Set PIN'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Biometric */}
        {bioAvailable && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <Fingerprint className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Biometric Unlock</p>
                  <p className="text-xs text-muted-foreground">
                    {hasBio ? 'Fingerprint / Face ID is active' : 'Use fingerprint or face to unlock'}
                  </p>
                </div>
              </div>
              {hasBio ? (
                <Button variant="outline" className="w-full" onClick={handleRemoveBiometric}>Remove Biometric</Button>
              ) : (
                <Button className="w-full gap-2" onClick={handleSetupBiometric} disabled={settingBio}>
                  <Fingerprint className="h-4 w-4" />
                  {settingBio ? 'Setting up…' : 'Enable Biometric'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Tag className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Categories</p>
                <p className="text-xs text-muted-foreground">Add or remove spending categories</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {data.categories.map(cat => (
                <Badge key={cat} variant="secondary" className="gap-1 pr-1">
                  {cat}
                  <button onClick={() => removeCategory(cat)} className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/20">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="New category"
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCategory()}
                className="flex-1"
              />
              <Button size="icon" onClick={addCategory}><Plus className="h-4 w-4" /></Button>
            </div>
          </CardContent>
        </Card>

        {/* Export */}
        {(() => {
          const backupStale = !lastBackup || (Date.now() - new Date(lastBackup).getTime()) > 7 * 24 * 60 * 60 * 1000;
          return (
            <Card className={backupStale ? 'border-destructive/50' : ''}>
              <CardContent className="p-4">
                {backupStale && (
                  <div className="flex items-center gap-2 mb-3 rounded-lg bg-destructive/10 p-2">
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                    <p className="text-xs font-medium text-destructive">
                      {lastBackup ? 'Your last backup is over 7 days old' : 'You haven\'t backed up your data yet'}
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${backupStale ? 'bg-destructive/10' : 'bg-primary/10'}`}>
                    <Download className={`h-5 w-5 ${backupStale ? 'text-destructive' : 'text-primary'}`} />
                  </div>
                  <div>
                    <p className="font-medium">Export Data</p>
                    <p className="text-xs text-muted-foreground">Download encrypted backup</p>
                  </div>
                </div>
                {lastBackup && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Last backed up: {new Date(lastBackup).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
                <Button onClick={handleExport} className="w-full">Export Encrypted Backup</Button>
              </CardContent>
            </Card>
          );
        })()}

        {/* Import */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Upload className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Import Data</p>
                <p className="text-xs text-muted-foreground">Restore from encrypted backup</p>
              </div>
            </div>
            <div className="space-y-2">
              <Input
                type="file"
                accept=".enc,.json"
                onChange={handleImportFile}
                className="text-sm"
              />
              {importFile && (
                <>
                  <Input
                    type="password"
                    placeholder="Backup password (leave blank if same)"
                    value={importPwd}
                    onChange={e => setImportPwd(e.target.value)}
                  />
                  <Button onClick={handleImport} className="w-full" disabled={importing}>
                    {importing ? 'Importing…' : 'Import & Restore'}
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>


        {/* Change Email */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Email Address</p>
                <p className="text-xs text-muted-foreground">{storedEmail || 'Not set'}</p>
              </div>
            </div>
            {emailEditing ? (
              <div className="space-y-2">
                <Input type="email" inputMode="email" placeholder="New email address"
                  value={emailEdit} onChange={e => setEmailEdit(e.target.value)} maxLength={254} autoFocus />
                <Input type="password" placeholder="Confirm with your password"
                  value={emailConfirmPwd} onChange={e => setEmailConfirmPwd(e.target.value)} />
                <div className="flex gap-2">
                  <Button className="flex-1 gap-1.5" onClick={handleSaveEmail} disabled={emailSaving}>
                    <Check className="h-4 w-4" />{emailSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => { setEmailEditing(false); setEmailEdit(''); setEmailConfirmPwd(''); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" className="w-full gap-1.5" onClick={() => { setEmailEdit(storedEmail); setEmailEditing(true); }}>
                <Pencil className="h-4 w-4" />Change Email
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardContent className="p-4">

            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <KeyRound className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Change Password / PIN</p>
                <p className="text-xs text-muted-foreground">Re-encrypts all data</p>
              </div>
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Current password"
                value={currentPwd}
                onChange={e => setCurrentPwd(e.target.value)}
              />
              <Input
                type="password"
                placeholder="New password"
                value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
              />
              <Button
                onClick={handleChangePassword}
                className="w-full"
                disabled={changingPwd || !currentPwd || !newPwd || !confirmPwd}
              >
                {changingPwd ? 'Changing…' : 'Change Password'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reset Achievements */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Reset Achievements</p>
                <p className="text-xs text-muted-foreground">Clear all earned badges and start fresh</p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full">Reset Achievements</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset all achievements?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear all your earned badges. You can earn them again from this point forward. Your data will not be affected.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleResetAchievements}>Reset Achievements</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Clear All Data */}
        <Card className="border-destructive/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
                <Trash2 className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <p className="font-medium text-destructive">Clear All Data</p>
                <p className="text-xs text-muted-foreground">Permanently delete everything and sign out</p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full">Clear All Data</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all your transactions, budgets, bills, assets, accounts, bank sync connections, and settings. You will be signed out. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClearAll}>Delete Everything</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
        {/* Version */}
        <p className="text-center text-[10px] text-muted-foreground/50 pt-2 select-all">
          Build: {typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev'}
        </p>
      </div>
    </div>
  );
}
